import React, { useState, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import {
  Flex,
  Text,
  Badge,
  Anchor,
  ActionIcon,
  NumberFormatter,
} from "@mantine/core";
import { IconWorld, IconHome } from "@tabler/icons-react";
import UserInteractionAccordion from "./UserInteractionAccordion";
import entityIcon from "@util/entityIcon";
import { getBadgeConfig } from "./helpers";
import { Link } from "react-router-dom";
import { IconExternalLink } from "@tabler/icons-react";
import formatVehicle from "@util/formatVehicle";

import hogLogo from "@assets/images/hog-logo-color.png";

export default function UserInteractionMiniDetail({ interactionId }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, [interactionId]);

  function fetchData() {
    if (!interactionId) return;

    axios
      .get(`/user-interactions/${interactionId}/`)
      .then(({ data }) => {
        setData(data.response[0]);
      })
      .catch((err) => {
        setData(null);
        setError(err);
      });
  }

  const interactionData = data
    ? [
        {
          text: new Date(data.occurred_at).toLocaleString(),
          icon: entityIcon.calendar(),
          badge: null,
          display: data.occurred_at,
        },
        {
          text: `${data.first_name} ${data.last_name}${
            data.user_created ? ` (${t("mixer:user:new_user")})` : ""
          }`,
          icon: entityIcon.user(),
          display: data.first_name,
          link_path: data.user_id ? `/users/${data.user_id}` : null,
        },
        {
          text: data.hog_member_number,
          icon: (
            <div
              style={{
                background: "#000",
                padding: "0 2px",
                borderRadius: "3px",
                width: "16px",
                height: "18px",
              }}
            >
              <img
                src={hogLogo}
                alt="HOG Logo"
                style={{
                  width: "100%",
                  height: "100%",
                  position: "relative",
                  objectFit: "contain",
                }}
              />
            </div>
          ),
          display: data.hog_member_number ? true : false,
        },
        {
          text: data.campaign_effort_name,
          icon: entityIcon.effort(),
          badge: null,
          display: data.campaign_effort_name,
          link_path: data.campaign_effort_id
            ? `/efforts/${data.campaign_effort_id}`
            : null,
        },
        {
          text: data.location_name,
          icon: entityIcon.location(),
          badge: data.present_at_location ? (
            <Badge variant="light" size="xs" color="green">
              {t("mixer:effort:present_at_location")}
            </Badge>
          ) : null,
          display: data.location_name,
          link_path: data.location_id ? `/locations/${data.location_id}` : null,
        },
        {
          text: data.keyword_text,
          icon: entityIcon.keyword(),
          display: data.keyword_text,
        },
        {
          text: `${data.entry_count} ${
            data.entry_count === 1
              ? t("mixer:entry_singular")
              : t("mixer:entry_plural")
          }`,
          icon: entityIcon.entry(),
          badge: null,
          display: ![0, undefined, null].includes(data.entry_count),
        },
        {
          text: data.organization_name,
          icon: entityIcon.organization(),
          badge: null,
          display: data.organization_name,
          link_path: data.organization_id
            ? `/organizations/${data.organization_id}`
            : null,
        },
        {
          text: data.campaign_name,
          icon: entityIcon.campaign(),
          badge: null,
          display: data.campaign_name && data.campaign_name !== "No Campaign",
          link_path: data.campaign_id ? `/campaigns/${data.campaign_id}` : null,
        },
        {
          text: formatVehicle(data),
          icon: entityIcon.rideChallenge(),
          display: data.vin ? true : false,
        },
        {
          text: (
            <>
              <NumberFormatter thousandSeparator value={data.odometer_value} />{" "}
              mi
            </>
          ),
          icon: entityIcon.rideChallenge(),
          display: data.odometer_value ? true : false,
        },
        {
          text:
            data.address_data && data.address_data.geocode_data
              ? data.address_data.geocode_data.formatted_address
              : "",
          icon: <IconHome size={16} />,
          badge: null,
          display:
            data.address_data &&
            data.address_data.geocode_data &&
            data.address_data.geocode_data.formatted_address,
        },
        {
          text: (
            <Anchor
              component="a"
              href={`https://www.google.com/maps/@${data.latitude},${data.longitude},14z`}
              target="_blank"
            >
              {data.latitude}, {data.longitude}
            </Anchor>
          ),
          icon: <IconWorld size={16} />,
          badge: null,
          display: data.latitude && data.longitude,
        },
      ].filter((f) => f.display)
    : [];

  if (error) {
    return (
      <Text fw={600} c="red">
        {error}
        <br />
        {t("common:interaction_singular")} ID: {interactionId}
      </Text>
    );
  }

  return (
    <div>
      {interactionData.map((m, i) => (
        <Flex gap="xs" align="center" mb="sm" key={i}>
          {m.icon}
          <Text size="md">{m.text}</Text>
          {m.link_path && (
            <ActionIcon
              component={Link}
              to={m.link_path}
              target="_blank"
              size="xs"
              color="gray"
              variant="light"
              radius="xl"
            >
              <IconExternalLink size={12} />
            </ActionIcon>
          )}
          {m.badge ? <>{m.badge}</> : null}
        </Flex>
      ))}
      {data && (
        <UserInteractionAccordion
          emailData={data.email_data}
          phoneData={data.phone_data}
        />
      )}
    </div>
  );
}
